/**
 * Structured medical timeline renderer.
 *
 * Displays a `MedicalTimeline` (see `api/documents.ts`) as a vertical
 * alternating react-chrono timeline with custom card content. The card
 * body is rendered via the `children` slot pattern documented by
 * react-chrono: the i-th child element maps to the i-th `items` entry.
 *
 * Filter chips above the timeline toggle event categories client-side
 * (no API refetch). The "Concluzii cheie" strip is collapsible. The
 * "Generat la" pill shows the relative age of the payload.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chrono } from 'react-chrono';
import type {
  MedicalTimeline as MedicalTimelineData,
  TimelineEvent,
  TimelineEventCategory,
  TimelineEventSeverity,
} from '../api/documents';
import './MedicalTimeline.css';

interface MedicalTimelineProps {
  data: MedicalTimelineData;
  /**
   * Optional initial severity allow-list. When set, only events whose
   * `severity` is included are visible on first render. The user can
   * still toggle severities via the legend dots once they're rendered
   * as filter chips. Pass an empty array to keep the default ("all").
   *
   * Used by the member detail Overview tab when navigating to the
   * cronologie tab via the "Vezi toate" CTA on Constatări importante.
   */
  initialSeverityFilter?: TimelineEventSeverity[];
}

// Category metadata: Romanian label + inline SVG icon (matches the
// app's existing iconography convention — no `lucide-react` here).
const CATEGORY_META: Record<
  TimelineEventCategory,
  { label: string; icon: React.ReactNode }
> = {
  lab: {
    label: 'Laborator',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M9 2v6L4.2 17.5A2 2 0 0 0 6 20h12a2 2 0 0 0 1.8-2.5L15 8V2" />
        <path d="M8 2h8" />
        <path d="M7 14h10" />
      </svg>
    ),
  },
  imaging: {
    label: 'Imagistică',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M3 7V5a2 2 0 0 1 2-2h2" />
        <path d="M17 3h2a2 2 0 0 1 2 2v2" />
        <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
        <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
        <line x1="7" y1="12" x2="17" y2="12" />
      </svg>
    ),
  },
  clinical: {
    label: 'Clinic',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M6 2v6a4 4 0 0 0 8 0V2" />
        <path d="M10 14v3a3 3 0 0 0 6 0v-1a4 4 0 0 1 4-4" />
        <circle cx="20" cy="12" r="1.5" />
      </svg>
    ),
  },
  procedure: {
    label: 'Procedură',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
  medication: {
    label: 'Medicație',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M10.5 20.5a4.95 4.95 0 0 1-7-7l10-10a4.95 4.95 0 1 1 7 7l-10 10z" />
        <line x1="8.5" y1="8.5" x2="15.5" y2="15.5" />
      </svg>
    ),
  },
  diagnosis: {
    label: 'Diagnostic',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M9 2h6a2 2 0 0 1 2 2v2H7V4a2 2 0 0 1 2-2z" />
        <rect x="4" y="6" width="16" height="16" rx="2" />
        <line x1="9" y1="12" x2="15" y2="12" />
        <line x1="9" y1="16" x2="15" y2="16" />
      </svg>
    ),
  },
  vaccination: {
    label: 'Vaccinare',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="m18 2 4 4" />
        <path d="m17 7 3-3" />
        <path d="M19 9 8.7 19.3a2 2 0 0 1-1.4.7H4v-3.3a2 2 0 0 1 .7-1.4L15 5" />
        <path d="m9 11 4 4" />
      </svg>
    ),
  },
  consultation: {
    label: 'Consultație',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
      </svg>
    ),
  },
  other: {
    label: 'Alte',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="9" />
      </svg>
    ),
  },
};

const SEVERITY_META: Record<TimelineEventSeverity, { label: string; color: string }> = {
  info: { label: 'Informativ', color: 'var(--color-slate-400)' },
  notable: { label: 'De notat', color: 'var(--color-info-500)' },
  important: { label: 'Important', color: 'var(--color-warning-500)' },
  critical: { label: 'Critic', color: 'var(--color-danger-500)' },
};

const ALL_CATEGORIES: TimelineEventCategory[] = [
  'lab',
  'imaging',
  'clinical',
  'procedure',
  'medication',
  'diagnosis',
  'vaccination',
  'consultation',
  'other',
];

/** Pretty-print a Romanian "Generat acum X" pill from an ISO datetime. */
function formatGeneratedAt(iso: string): string {
  try {
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return iso;
    const diffMs = Date.now() - then.getTime();
    const min = Math.round(diffMs / 60_000);
    if (min < 1) return 'Generat acum';
    if (min < 60) return `Generat acum ${min} ${min === 1 ? 'minut' : 'minute'}`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `Generat acum ${hr} ${hr === 1 ? 'oră' : 'ore'}`;
    const days = Math.round(hr / 24);
    if (days < 30) return `Generat acum ${days} ${days === 1 ? 'zi' : 'zile'}`;
    return `Generat la ${then.toLocaleDateString('ro-RO', { year: 'numeric', month: 'short', day: 'numeric' })}`;
  } catch {
    return iso;
  }
}

/** ISO date → "12 mar. 2024" (Romanian short). */
function formatEventDate(iso: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('ro-RO', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

/** Free-form analyte status (Romanian) → CSS class for colouring. */
function analyteStatusClass(status?: string | null): string {
  if (!status) return '';
  const s = status.toLowerCase();
  if (s.includes('scăz') || s.includes('scaz') || s.includes('low')) return 'mt-status-low';
  if (s.includes('ridic') || s.includes('high') || s.includes('crescu')) return 'mt-status-high';
  if (s.includes('critic')) return 'mt-status-critical';
  if (s.includes('normal') || s.includes('ok')) return 'mt-status-normal';
  return '';
}

const ALL_SEVERITIES: TimelineEventSeverity[] = ['info', 'notable', 'important', 'critical'];

const MedicalTimeline: React.FC<MedicalTimelineProps> = ({ data, initialSeverityFilter }) => {
  const navigate = useNavigate();
  // The category filter: empty set = "all visible".
  const [hiddenCats, setHiddenCats] = useState<Set<TimelineEventCategory>>(new Set());
  // Severity filter: empty set = "all visible". Pre-populated from the
  // optional `initialSeverityFilter` prop (e.g. "show only important +
  // critical" when the user arrives from the Overview tab).
  const [hiddenSevs, setHiddenSevs] = useState<Set<TimelineEventSeverity>>(() => {
    if (!initialSeverityFilter || initialSeverityFilter.length === 0) return new Set();
    const allow = new Set(initialSeverityFilter);
    return new Set(ALL_SEVERITIES.filter((s) => !allow.has(s)));
  });
  const [highlightsOpen, setHighlightsOpen] = useState(true);

  const visibleEvents = useMemo<TimelineEvent[]>(() => {
    return data.events.filter(
      (ev) => !hiddenCats.has(ev.category) && !hiddenSevs.has(ev.severity),
    );
  }, [data.events, hiddenCats, hiddenSevs]);

  // Counts per category — for the chip badges.
  const countsByCat = useMemo(() => {
    const m = new Map<TimelineEventCategory, number>();
    for (const ev of data.events) m.set(ev.category, (m.get(ev.category) || 0) + 1);
    return m;
  }, [data.events]);

  const toggleCategory = (cat: TimelineEventCategory) => {
    setHiddenCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const toggleSeverity = (sev: TimelineEventSeverity) => {
    setHiddenSevs((prev) => {
      const next = new Set(prev);
      if (next.has(sev)) next.delete(sev);
      else next.add(sev);
      return next;
    });
  };

  const hasSeverityFilter = hiddenSevs.size > 0;
  const clearSeverityFilter = () => setHiddenSevs(new Set());

  // react-chrono `items` is in lockstep with the children array. We
  // pass minimal items here (date + a placeholder title) and render
  // the real card body via the children slot.
  const chronoItems = useMemo(
    () =>
      visibleEvents.map((ev) => ({
        title: formatEventDate(ev.date),
        // `id` would conflict with the `noUniqueId` machinery if we
        // weren't passing it; safest to omit and let react-chrono
        // generate ones.
      })),
    [visibleEvents],
  );

  const theme = {
    primary: 'var(--color-primary-600)',
    secondary: 'var(--color-primary-50)',
    cardBgColor: 'var(--surface-card)',
    cardForeColor: 'var(--color-slate-700)',
    titleColor: 'var(--color-slate-600)',
    titleColorActive: 'var(--color-primary-700)',
  };

  if (data.events.length === 0) {
    return (
      <div className="mt-empty" role="status">
        Modelul nu a returnat evenimente pentru acest membru. Încearcă să regenerezi cronologia.
      </div>
    );
  }

  return (
    <div className="medical-timeline">
      {/* Header strip: generated-at + total + filter chips */}
      <div className="mt-header">
        <div className="mt-header-meta">
          <span className="mt-pill mt-pill-generated" title={data.generated_at}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            {formatGeneratedAt(data.generated_at)}
          </span>
          <span className="mt-pill mt-pill-count">
            {visibleEvents.length === data.total_events
              ? `${data.total_events} evenimente`
              : `${visibleEvents.length} / ${data.total_events} evenimente`}
          </span>
          {data.date_range.from && data.date_range.to && (
            <span className="mt-pill mt-pill-range">
              {formatEventDate(data.date_range.from)} — {formatEventDate(data.date_range.to)}
            </span>
          )}
        </div>

        <div className="mt-chips" role="group" aria-label="Filtre categorie">
          {ALL_CATEGORIES.map((cat) => {
            const meta = CATEGORY_META[cat];
            const count = countsByCat.get(cat) || 0;
            if (count === 0) return null;
            const active = !hiddenCats.has(cat);
            return (
              <button
                key={cat}
                type="button"
                className={`mt-chip mt-chip-${cat} ${active ? 'is-active' : 'is-muted'}`}
                onClick={() => toggleCategory(cat)}
                aria-pressed={active}
                title={active ? `Ascunde ${meta.label.toLowerCase()}` : `Arată ${meta.label.toLowerCase()}`}
              >
                <span className="mt-chip-icon">{meta.icon}</span>
                <span className="mt-chip-label">{meta.label}</span>
                <span className="mt-chip-count">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-legend" aria-label="Legendă severitate" role="group">
          {(Object.keys(SEVERITY_META) as TimelineEventSeverity[]).map((sev) => {
            const active = !hiddenSevs.has(sev);
            return (
              <button
                key={sev}
                type="button"
                className={`mt-legend-item${active ? '' : ' is-muted'}`}
                onClick={() => toggleSeverity(sev)}
                aria-pressed={active}
                title={active ? `Ascunde ${SEVERITY_META[sev].label.toLowerCase()}` : `Arată ${SEVERITY_META[sev].label.toLowerCase()}`}
              >
                <span
                  className="mt-legend-dot"
                  style={{ background: active ? SEVERITY_META[sev].color : 'var(--color-slate-300)' }}
                  aria-hidden
                />
                {SEVERITY_META[sev].label}
              </button>
            );
          })}
          {hasSeverityFilter && (
            <button
              type="button"
              className="mt-legend-clear"
              onClick={clearSeverityFilter}
            >
              Resetează
            </button>
          )}
        </div>
      </div>

      {/* Highlights strip (collapsible) */}
      {data.highlights.length > 0 && (
        <div className={`mt-highlights ${highlightsOpen ? 'is-open' : 'is-closed'}`}>
          <button
            type="button"
            className="mt-highlights-toggle"
            onClick={() => setHighlightsOpen((v) => !v)}
            aria-expanded={highlightsOpen}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              {highlightsOpen ? (
                <polyline points="6 9 12 15 18 9" />
              ) : (
                <polyline points="9 6 15 12 9 18" />
              )}
            </svg>
            Concluzii cheie ({data.highlights.length})
          </button>
          {highlightsOpen && (
            <ul className="mt-highlights-list">
              {data.highlights.map((h, i) => (
                <li key={i}>{h}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* react-chrono body */}
      {visibleEvents.length === 0 ? (
        <div className="mt-empty mt-empty-filtered" role="status">
          Toate categoriile sunt ascunse. Activează cel puțin una pentru a vedea evenimente.
        </div>
      ) : (
        <div className="mt-chrono-wrap">
          <Chrono
            key={visibleEvents.map((e) => e.id).join('|')}
            items={chronoItems}
            mode="VERTICAL_ALTERNATING"
            theme={theme}
            cardHeight={'auto'}
            disableToolbar
            hideControls
            // The timeline lives inside an unbounded page; we never want
            // its internal viewport to scroll. Setting `scrollable` to
            // `false` removes react-chrono's virtualised scroll behaviour
            // and lets the wrapper grow with content. We additionally
            // neutralise the lingering `overflow-y:auto` baked into the
            // `TimelineMainWrapper` styled-component via CSS overrides
            // in `MedicalTimeline.css`.
            scrollable={false}
            useReadMore={false}
            disableClickOnCircle
            timelinePointDimension={20}
          >
            {visibleEvents.map((ev) => (
              <MedicalTimelineCard
                key={ev.id}
                event={ev}
                onOpenDocument={(docId) => navigate(`/documents/${docId}`)}
              />
            ))}
          </Chrono>
        </div>
      )}
    </div>
  );
};

interface CardProps {
  event: TimelineEvent;
  onOpenDocument: (documentId: number) => void;
}

const MedicalTimelineCard: React.FC<CardProps> = ({ event, onOpenDocument }) => {
  const catMeta = CATEGORY_META[event.category] || CATEGORY_META.other;
  const sevMeta = SEVERITY_META[event.severity] || SEVERITY_META.info;
  const firstDoc = event.source_document_ids[0];
  const extraDocs = event.source_document_ids.length - 1;

  return (
    <div className={`mt-card mt-card-${event.category} mt-card-sev-${event.severity}`}>
      <div className="mt-card-header">
        <span
          className="mt-card-icon"
          style={{ color: sevMeta.color }}
          aria-label={`${catMeta.label}, severitate ${sevMeta.label.toLowerCase()}`}
        >
          {catMeta.icon}
        </span>
        <div className="mt-card-titles">
          <h4 className="mt-card-title" title={event.title}>
            {event.title}
          </h4>
          <div className="mt-card-subtitle">
            <span className="mt-card-cat">{catMeta.label}</span>
            {event.specialty && <span className="mt-card-sep">·</span>}
            {event.specialty && <span>{event.specialty}</span>}
            <span className="mt-card-sep">·</span>
            <span>{formatEventDate(event.date)}</span>
          </div>
        </div>
        <span
          className="mt-card-sev-dot"
          style={{ background: sevMeta.color }}
          title={sevMeta.label}
          aria-hidden
        />
      </div>

      {event.summary && <p className="mt-card-summary">{event.summary}</p>}

      {event.body_regions.length > 0 && (
        <div className="mt-card-badges" aria-label="Regiuni anatomice">
          {event.body_regions.map((r, i) => (
            <span key={`${r}-${i}`} className="mt-badge mt-badge-region">
              {r}
            </span>
          ))}
        </div>
      )}

      {event.related_conditions.length > 0 && (
        <div className="mt-card-badges" aria-label="Afecțiuni asociate">
          {event.related_conditions.map((c, i) => (
            <span key={`${c}-${i}`} className="mt-badge mt-badge-condition">
              {c}
            </span>
          ))}
        </div>
      )}

      {event.analytes.length > 0 && (
        <table className="mt-analytes" aria-label="Analiți">
          <thead>
            <tr>
              <th>Analit</th>
              <th>Valoare</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {event.analytes.map((a, i) => (
              <tr key={`${a.name}-${i}`}>
                <td>{a.name}</td>
                <td>
                  {a.value ?? '—'}
                  {a.unit ? ` ${a.unit}` : ''}
                </td>
                <td className={analyteStatusClass(a.status)}>{a.status || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {firstDoc !== undefined && (
        <div className="mt-card-footer">
          <button
            type="button"
            className="mt-link"
            onClick={() => onOpenDocument(firstDoc)}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            Vezi document
            {extraDocs > 0 && <span className="mt-doc-extra">+{extraDocs}</span>}
          </button>
        </div>
      )}
    </div>
  );
};

export default MedicalTimeline;
